import { isSameDay } from 'date-fns'
import { useContext, useMemo } from 'react'
import { LocaleContext } from 'components/common/LocaleContext'
import { AutocompleteItem } from 'interfaces/algolia'

type AutocompleteItemEventCardDisplayedDate = Pick<
  AutocompleteItem,
  | 'editionLiveStartDateTimestamp'
  | 'editionLiveEndDateTimestamp'
  | 'editionCalendarStatus'
>

export const useEventCardDisplayedDate = (
  item: AutocompleteItemEventCardDisplayedDate
) => {
  const { dateStringLocale } = useContext(LocaleContext)

  const {
    editionLiveStartDateTimestamp,
    editionLiveEndDateTimestamp,
    editionCalendarStatus
  } = item

  const startDate = editionLiveStartDateTimestamp
    ? new Date(editionLiveStartDateTimestamp * 1000)
    : null
  const endDate = editionLiveEndDateTimestamp
    ? new Date(editionLiveEndDateTimestamp * 1000)
    : null

  const isDateConfirmed = editionCalendarStatus !== 'TO_BE_CONFIRMED'
  const isEndDateDisplayed =
    isDateConfirmed && startDate && endDate && !isSameDay(startDate, endDate)

  const endYear =
    endDate &&
    new Date(endDate).toLocaleDateString(dateStringLocale, {
      year: 'numeric'
    })

  const endMonth =
    endDate &&
    new Date(endDate).toLocaleDateString(dateStringLocale, {
      month: 'long'
    })

  const endDay =
    endDate &&
    new Date(endDate).toLocaleDateString(dateStringLocale, {
      day: 'numeric'
    })
  const startYear =
    startDate &&
    new Date(startDate).toLocaleDateString(dateStringLocale, {
      year: 'numeric'
    })
  const startMonth =
    startDate &&
    new Date(startDate).toLocaleDateString(dateStringLocale, {
      month: 'long'
    })
  const startDay =
    startDate &&
    new Date(startDate).toLocaleDateString(dateStringLocale, {
      day: 'numeric'
    })

  const dateDisplayText = useMemo(() => {
    if (isEndDateDisplayed) {
      let dayPart = `${startDay}`
      if (startMonth !== endMonth || startYear !== endYear) {
        dayPart += ` ${startMonth}`
      }
      if (startYear !== endYear) {
        dayPart += ` ${startYear}`
      }
      dayPart += ` - ${endDay}`
      return `${dayPart} ${endMonth} ${endYear}`
    } else {
      const dayPart = isDateConfirmed ? `${startDay} ` : ''
      return `${dayPart}${startMonth} ${startYear}`
    }
  }, [
    isEndDateDisplayed,
    startDay,
    startMonth,
    startYear,
    endDay,
    endMonth,
    endYear,
    isDateConfirmed
  ])

  return {
    dateDisplayText,
    startDate,
    endDate
  }
}
